import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { yupConstants } from "../../utils/constants/yup";
import { InputFileCustom } from "..";
import {
  communicationActionTypeOptions,
  communicationAppearanceColorOptions,
} from "../../utils/constants/institutional-communications";
import {
  InstitutionalCommunication,
  InstitutionalCommunicationFetch,
  CommunicationActionType,
} from "../../models/institutional-communications";
import {
  useCreateInstitutionalCommunicationMutation,
  useUpdateInstitutionalCommunicationMutation,
} from "../../store/services/institutional-communications";
import { useGetTenantsQuery } from "../../store/services/tenants";
import { useTenantScope } from "../../hooks/useTenantScope";

interface InstitutionalCommunicationFormProps {
  communicationData?: InstitutionalCommunication;
}

interface InstitutionalCommunicationFormValues {
  id?: number;
  title: string;
  communicationType: string;
  mainDescription: string;
  importantMessage: string;
  additionalInformation: string;
  actionType: CommunicationActionType;
  redirectUrl: string;
  appearanceColor: InstitutionalCommunicationFetch["appearanceColor"];
  isActive: boolean;
  imageFormat: string;
  imageBase64: string;
  tenantId: string;
}

export const InstitutionalCommunicationForm = ({
  communicationData,
}: InstitutionalCommunicationFormProps) => {
  const navigate = useNavigate();

  const { data: tenants, isLoading: isLoadingTenants } = useGetTenantsQuery({});
  const tenantScope = useTenantScope();

  const [createInstitutionalCommunication, { isLoading: isCreatingCommunication }] =
    useCreateInstitutionalCommunicationMutation();

  const [updateInstitutionalCommunication, { isLoading: isUpdatingCommunication }] =
    useUpdateInstitutionalCommunicationMutation();

  const isSubmitting = isCreatingCommunication || isUpdatingCommunication;

  const goBack = () => navigate(-1);

  const {
    handleSubmit,
    touched,
    errors,
    values,
    handleBlur,
    handleChange,
    setFieldValue,
    setFieldError,
    validateForm,
  } = useFormik<InstitutionalCommunicationFormValues>({
    initialValues: {
      id: communicationData?.id,
      title: communicationData?.name || "",
      communicationType: communicationData?.type || "",
      mainDescription: communicationData?.description || "",
      importantMessage: communicationData?.importantMessage || "",
      additionalInformation: communicationData?.additionalInformation || "",
      actionType: communicationData?.actionType || "EXTERNAL_URL",
      redirectUrl: communicationData?.redirectUrl || "",
      appearanceColor: communicationData?.appearanceColor || "PRIMARY",
      isActive: communicationData?.status
        ? communicationData.status === "ACTIVE"
        : true,
      imageFormat: communicationData?.imageFormat || "",
      imageBase64: communicationData?.imageBase64 || "",
      tenantId: communicationData?.tenantId || "",
    },
    validationSchema: Yup.object({
      title: Yup.string().required(yupConstants().required),
      communicationType: Yup.string().required(yupConstants().required),
      mainDescription: Yup.string().required(yupConstants().required),
      imageFormat: Yup.string().required(yupConstants().required),
      imageBase64: Yup.string().required(yupConstants().required),
      actionType: Yup.mixed<CommunicationActionType>()
        .oneOf(["OPEN_DETAIL", "EXTERNAL_URL"])
        .required(yupConstants().required),
      redirectUrl: Yup.string().when("actionType", {
        is: "EXTERNAL_URL",
        then: (schema) =>
          schema.required(yupConstants().required),
        otherwise: (schema) => schema.notRequired(),
      }),
      appearanceColor: Yup.string().required(yupConstants().required),
      tenantId: Yup.string().nullable().optional(),
    }),
    onSubmit: async (formValues) => {
      const communicationPayload: InstitutionalCommunicationFetch = {
        id: formValues.id,
        name: formValues.title,
        type: formValues.communicationType,
        description: formValues.mainDescription,
        imageFormat: formValues.imageFormat,
        imageBase64: formValues.imageBase64,
        status: formValues.isActive ? "ACTIVE" : "INACTIVE",
        eventDateTime: communicationData?.eventDateTime || new Date().toISOString(),
        additionalInformation:
          formValues.actionType === "OPEN_DETAIL"
            ? formValues.additionalInformation || null
            : null,
        importantMessage:
          formValues.actionType === "OPEN_DETAIL"
            ? formValues.importantMessage || null
            : null,
        actionType: formValues.actionType,
        redirectUrl:
          formValues.actionType === "EXTERNAL_URL"
            ? formValues.redirectUrl
            : null,
        appearanceColor: formValues.appearanceColor,
        tenantId: formValues.tenantId || null,
      };

      try {
        if (communicationData?.id) {
          await updateInstitutionalCommunication(communicationPayload).unwrap();
          toast.success("Comunicación actualizada exitosamente");
        } else {
          await createInstitutionalCommunication(communicationPayload).unwrap();
          toast.success("Comunicación creada exitosamente");
        }
        goBack();
      } catch (error: any) {
        console.error("Error guardando comunicación institucional:", error);
        console.error("Payload enviado:", communicationPayload);
        const zodIssues = error?.data?.error?.issues;
        if (zodIssues && Array.isArray(zodIssues)) {
          const details = zodIssues.map((i: any) => `${i.path?.join(".")}: ${i.message}`).join(", ");
          toast.error(`Error de validación: ${details}`);
        } else {
          const msg = error?.data?.message || error?.message || "Error desconocido";
          toast.error(`No se pudo guardar: ${msg}`);
        }
      }
    },
  });

  // MT-011 — Si el tenant está fijado por el scope, forzar el valor del form.
  React.useEffect(() => {
    if (tenantScope.locked && tenantScope.tenantId && values.tenantId !== tenantScope.tenantId) {
      setFieldValue("tenantId", tenantScope.tenantId);
    }
  }, [tenantScope.locked, tenantScope.tenantId, values.tenantId, setFieldValue]);

  const handleImageUpload = (encodedImage: string) => {
    let parsedImageFormat = "";
    let resolvedImageBase64 = encodedImage;

    if (encodedImage.startsWith("data:image/")) {
      // Base64 data URI: data:image/jpeg;base64,...
      const [imageHeader, imageBase64] = encodedImage.split(",");
      parsedImageFormat = imageHeader?.split("image/")[1]?.split(";")[0] || "";
      resolvedImageBase64 = imageBase64 || encodedImage;
    } else {
      // S3 URL: extract format from file extension
      const extensionMatch = encodedImage.match(/\.(\w+)(?:\?.*)?$/);
      parsedImageFormat = extensionMatch?.[1]?.toLowerCase() || "jpeg";
      if (parsedImageFormat === "jpg") parsedImageFormat = "jpeg";
      resolvedImageBase64 = encodedImage;
    }

    setFieldError("imageFormat", undefined);
    setFieldError("imageBase64", undefined);
    setFieldValue("imageFormat", parsedImageFormat, true);
    setFieldValue("imageBase64", resolvedImageBase64, true);
  };

  const clearUploadedImage = () => {
    setFieldValue("imageFormat", "", true);
    setFieldValue("imageBase64", "", true);
  };

  const imageErrorMessage =
    values.imageBase64 && values.imageFormat
      ? undefined
      : (errors.imageBase64 && errors.imageBase64) ||
        (errors.imageFormat && errors.imageFormat);

  const communicationImagePreview = values.imageBase64
    ? values.imageBase64.startsWith("data:image/")
      ? values.imageBase64
      : values.imageBase64.startsWith("http")
      ? values.imageBase64
      : `data:image/${values.imageFormat || "jpeg"};base64,${values.imageBase64}`
    : communicationData?.mainImageUrl || "";

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationErrors = await validateForm();
    if (Object.keys(validationErrors).length > 0) {
      console.error("Errores de validación Formik:", validationErrors);
      const errorMessages = Object.entries(validationErrors)
        .map(([field, msg]) => `${field}: ${msg}`)
        .join(", ");
      toast.error(`Errores en el formulario: ${errorMessages}`);
      return;
    }
    handleSubmit(e);
  };

  return (
    <form className="grid" onSubmit={handleFormSubmit}>
      <div className="col-12 md:col grid">
        <div className="col-12 my-2">
          <span className="p-float-label">
            <InputText
              invalid={!!touched.title && !!errors.title}
              className="w-full"
              id="title"
              name="title"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.title}
              disabled={isSubmitting}
            />
            <label htmlFor="title">Nombre</label>
          </span>
          {touched.title && errors.title && (
            <small className="p-error">{errors.title}</small>
          )}
        </div>

        <div className="col-12 md:col-6 my-2">
          <span className="p-float-label">
            <InputText
              invalid={!!touched.communicationType && !!errors.communicationType}
              className="w-full"
              id="communicationType"
              name="communicationType"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.communicationType}
              disabled={isSubmitting}
            />
            <label htmlFor="communicationType">Tipo</label>
          </span>
          {touched.communicationType && errors.communicationType && (
            <small className="p-error">{errors.communicationType}</small>
          )}
        </div>

        {!tenantScope.hidden && (
          <div className="col-12 md:col-6 my-2">
            <span className="p-float-label">
              <Dropdown
                invalid={!!touched.tenantId && !!errors.tenantId}
                className="w-full"
                id="tenantId"
                name="tenantId"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.tenantId}
                optionLabel="displayName"
                optionValue="id"
                options={Array.isArray(tenants) ? tenants.map(tenant => ({
                  ...tenant,
                  displayName: `${tenant.provider || tenant.id} ${tenant.emailDomain ? `(${tenant.emailDomain})` : ''}`.trim()
                })) : []}
                loading={isLoadingTenants}
                disabled={isSubmitting || tenantScope.locked}
                placeholder="Seleccione un tenant (opcional)"
                showClear={!tenantScope.locked}
              />
              <label htmlFor="tenantId">Tenant (Opcional)</label>
            </span>
            {tenantScope.locked && (
              <small className="text-color-secondary">
                Fijado por el tenant seleccionado en la barra superior.
              </small>
            )}
            {touched.tenantId && errors.tenantId && (
              <small className="p-error">{errors.tenantId}</small>
            )}
          </div>
        )}

        <div className="col-12 md:col-6 my-2">
          <span className="p-float-label">
            <Dropdown
              invalid={!!touched.appearanceColor && !!errors.appearanceColor}
              className="w-full"
              id="appearanceColor"
              name="appearanceColor"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.appearanceColor}
              optionLabel="label"
              optionValue="value"
              options={communicationAppearanceColorOptions}
              disabled={isSubmitting}
            />
            <label htmlFor="appearanceColor">Apariencia</label>
          </span>
          {touched.appearanceColor && errors.appearanceColor && (
            <small className="p-error">{errors.appearanceColor}</small>
          )}
        </div>

        <div className="col-12 my-2">
          <span className="p-label">
            <label htmlFor="mainDescription">Descripción</label>
            <InputTextarea
              className={`w-full ${
                touched.mainDescription && errors.mainDescription ? "p-invalid" : ""
              }`}
              id="mainDescription"
              name="mainDescription"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.mainDescription}
              rows={4}
              autoResize
              disabled={isSubmitting}
            />
          </span>
          {touched.mainDescription && errors.mainDescription && (
            <small className="p-error">{errors.mainDescription}</small>
          )}
        </div>

        <div className="col-12 md:col-6 my-2">
          <span className="p-float-label">
            <Dropdown
              invalid={!!touched.actionType && !!errors.actionType}
              className="w-full"
              id="actionType"
              name="actionType"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.actionType}
              optionLabel="label"
              optionValue="value"
              options={communicationActionTypeOptions}
              disabled={isSubmitting}
            />
            <label htmlFor="actionType">Tipo de acción</label>
          </span>
          {touched.actionType && errors.actionType && (
            <small className="p-error">{errors.actionType}</small>
          )}
        </div>

        {values.actionType === "EXTERNAL_URL" && (
          <div className="col-12 my-2">
            <span className="p-float-label">
              <InputText
                invalid={!!touched.redirectUrl && !!errors.redirectUrl}
                className="w-full"
                id="redirectUrl"
                name="redirectUrl"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.redirectUrl}
                disabled={isSubmitting}
              />
              <label htmlFor="redirectUrl">URL de redirección</label>
            </span>
            {touched.redirectUrl && errors.redirectUrl && (
              <small className="p-error">{errors.redirectUrl}</small>
            )}
          </div>
        )}

        <div className="col-12 md:col-6 my-2 flex align-items-center">
          <div className="flex align-items-center gap-2">
            <label htmlFor="isActive">Estado (Activo)</label>
            <InputSwitch
              id="isActive"
              checked={values.isActive}
              onChange={(event) => setFieldValue("isActive", event.value)}
              disabled={isSubmitting}
            />
          </div>
        </div>

        {values.actionType === "OPEN_DETAIL" && (
          <>
            <div className="col-12 my-2">
              <span className="p-label">
                <label htmlFor="importantMessage">Mensaje importante</label>
                <InputTextarea
                  className="w-full"
                  id="importantMessage"
                  name="importantMessage"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.importantMessage}
                  rows={2}
                  autoResize
                  disabled={isSubmitting}
                />
              </span>
            </div>
            <div className="col-12 my-2">
              <span className="p-label">
                <label htmlFor="additionalInformation">Información adicional</label>
                <InputTextarea
                  className="w-full"
                  id="additionalInformation"
                  name="additionalInformation"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.additionalInformation}
                  rows={3}
                  autoResize
                  disabled={isSubmitting}
                />
              </span>
            </div>
          </>
        )}
      </div>

      <div className="col-12 md:col-5 my-2 flex flex-column align-items-center justify-content-center">
        <InputFileCustom
          handleInputFile={handleImageUpload}
          handleInputFileClear={clearUploadedImage}
          error={imageErrorMessage}
          initialImage={communicationImagePreview}
        />
      </div>

      <div className="col-12 flex justify-content-center gap-4 mt-4">
        <Button
          icon="pi pi-check"
          severity="success"
          type="submit"
          label={communicationData ? "Actualizar" : "Crear"}
          loading={isSubmitting}
        />
        <Button
          icon="pi pi-times"
          severity="secondary"
          type="button"
          label="Cancelar"
          onClick={goBack}
          loading={isSubmitting}
        />
      </div>
    </form>
  );
};
